/**
 * UTM attribution for the SDK (WEG-7657).
 *
 * The SDK forwards `utm_*` from two sources, and **deliberately does not read or write
 * cookies on your domain.**
 *
 *   1. `opts.utmParams` — explicit values you pass. Always win.
 *   2. The embedding page's live URL.
 *
 * ### Why persistence is your job, not ours
 *
 * Last-touch-only attribution loses the donor who arrives from a campaign link, browses
 * until the query string is gone, and then clicks "Pay With WeGive". Closing that gap
 * needs a cookie — and on your domain, you are strictly better equipped to own it than
 * we are. You know your cookie names, their scope, your TTL policy, and your visitor's
 * consent state. The SDK, running inside your page, would be guessing at all four.
 *
 * Writing canonical `utm_*` cookies from here would also mean overwriting the values
 * your own marketing tooling set, and — on a host that embeds more than one
 * organization's pay button — sharing one unscoped cookie jar across organizations, with
 * no way to tell whose campaign a stored value belonged to.
 *
 * So resolve attribution yourself and hand us the answer:
 *
 * ```js
 * const utm = {}
 * for (const key of WeGive.Pay.UTM_PARAMS) {
 *   const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'))
 *   if (match) utm[key] = decodeURIComponent(match[1])
 * }
 *
 * pay.mountButton('#give', { amount: 5000, label: 'Give', utmParams: utm })
 * ```
 *
 * `UTM_PARAMS` is exported so the canonical key list stays in sync with what the API
 * stores. Values are trimmed and clamped to 255 characters to match the column width.
 *
 * The hosted popup at `pay.wegive.com` could not do any of this itself: it is a
 * WeGive-owned registrable domain, cross-site to your page, so it can never read your
 * cookies. That is why resolution happens here on the parent page and rides the popup
 * query string.
 *
 * **Never merge.** A UTM set describes a single marketing touch, so whichever source has
 * values wins as a whole set — pairing `utm_source` from one source with `utm_campaign`
 * from another would record a combination that never occurred.
 */
import type { UtmParam } from './types'
import { UTM_PARAMS } from './protocol'

export type UtmSet = Partial<Record<UtmParam, string>>

/** Matches the API column width; an oversized value would be rejected. */
const MAX_VALUE_LENGTH = 255

const isUtmParam = (value: string): value is UtmParam =>
  (UTM_PARAMS as readonly string[]).includes(value)

/**
 * Clamp to `MAX_VALUE_LENGTH` without splitting a surrogate pair.
 *
 * `String.length` counts UTF-16 code units, so a plain slice can land between the
 * halves of an astral character (emoji, some CJK extensions) and leave a lone high
 * surrogate, which serializes into the popup query as mojibake. Dropping the dangling
 * half costs one character and keeps the value valid.
 */
function truncateToMaxLength(value: string): string {
  if (value.length <= MAX_VALUE_LENGTH) return value

  const clamped = value.slice(0, MAX_VALUE_LENGTH)
  const lastUnit = clamped.charCodeAt(clamped.length - 1)
  const endsOnHighSurrogate = lastUnit >= 0xd800 && lastUnit <= 0xdbff

  return endsOnHighSurrogate ? clamped.slice(0, -1) : clamped
}

function normalizeValue(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  return truncateToMaxLength(trimmed)
}

/** Keep only known keys with usable values. */
export function sanitizeUtmSet(input: Record<string, unknown>): UtmSet {
  const out: UtmSet = {}

  for (const [key, raw] of Object.entries(input)) {
    if (!isUtmParam(key)) continue
    const value = normalizeValue(raw)
    if (value === null) continue
    out[key] = value
  }

  return out
}

/** `utm_*` on the given query string (accepts `?a=b` or `a=b`). */
export function parseUtmFromSearch(search: string): UtmSet {
  if (!search) return {}

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const found: Record<string, unknown> = {}

  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value !== null) found[key] = value
  }

  return sanitizeUtmSet(found)
}

/** `utm_*` on the embedding page's current URL. */
export function readLiveUrlUtm(): UtmSet {
  if (typeof window === 'undefined') return {}
  return parseUtmFromSearch(window.location?.search ?? '')
}

/**
 * Pick the effective UTM set: the first source carrying any value wins outright.
 *
 * Sources are given highest precedence first and are **never merged**. A UTM set
 * describes a single marketing touch, so taking `utm_source` from one source and
 * `utm_campaign` from another would fabricate a touch that never happened. Explicit
 * params carrying only `utm_campaign` therefore yield exactly `{utm_campaign}` — the
 * page URL does not backfill the remaining keys.
 *
 * Returns a copy, so callers cannot mutate a source through the result.
 */
export function selectUtmSet(...sources: UtmSet[]): UtmSet {
  for (const source of sources) {
    if (Object.keys(source).length > 0) {
      return { ...source }
    }
  }

  return {}
}

/**
 * The effective UTM set for a checkout: the values you pass explicitly, otherwise
 * whatever is on the live page URL.
 *
 * Whichever source has values first wins as a whole set; the two are never merged
 * together. See `selectUtmSet`.
 */
export function resolveUtmSet(explicit?: UtmSet | null): UtmSet {
  const explicitSet = explicit ? sanitizeUtmSet(explicit as Record<string, unknown>) : {}

  return selectUtmSet(explicitSet, readLiveUrlUtm())
}
